import React, {
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
  useContext,
} from "react";
import PropTypes from "prop-types";
import { Paginator } from "primereact/paginator";
import { AdvanceFilter } from "../../M_Components/M_Table/advanceFilter/AdvanceFilter";
import { MCheckBox } from "src/M_Components";

import { useTranslation } from "react-i18next";
import {
  getCellColor,
  headerCellStyle,
  cellStyle,
} from "../tableHelpers/TableStyleUtils";
import { exportGroupedExcel } from "../tableHelpers/TableExcelUtils";
import TableHeader from "../tableHelpers/TableHeader";
import ColumnMenu from "../tableHelpers/ColumnMenu";
import ViewsMenu from "../tableHelpers/ViewMenu";

import { LoadingContext } from "src/M_Components";
import "./CarrierResponseDetailTable.css";

//region Helpers

/**
 * Normalizes any metric-like value to a finite number.
 *
 * @param {any} value - Raw metric value.
 * @returns {number|null} Finite numeric value or null when absent/invalid.
 */
const toFiniteNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Reads the comparison value from a quote according to the selected metric.
 *
 * Supported metrics:
 * - unit  => UnitPrice
 * - bid   => BidValue
 * - loads => Loads
 * - etd   => EstimatedTransitDays
 *
 * @param {object} quote - Quote DTO for a single carrier/lane.
 * @param {"unit"|"bid"|"loads"|"etd"} field - Logical field name to read.
 * @returns {number|null} Normalized numeric value or null when absent.
 */
const getQuoteMetricValue = (quote, field) => {
  if (!quote) return null;

  switch (field) {
    case "unit":
      return toFiniteNumber(quote.UnitPrice ?? quote.unitPrice);

    case "bid":
      return toFiniteNumber(quote.BidValue ?? quote.bidValue);

    case "loads":
      return toFiniteNumber(quote.Loads ?? quote.loads);

    case "etd":
      return toFiniteNumber(
        quote.EstimatedTransitDays ?? quote.estimatedTransitDays
      );

    default:
      return null;
  }
};

/**
 * Reads the raw BidValue from a quote.
 *
 * @param {object} quote - Quote DTO.
 * @returns {number|null} Raw BidValue or null.
 */
const getQuoteBidValue = (quote) => {
  if (!quote) return null;
  return toFiniteNumber(quote.BidValue ?? quote.bidValue);
};

/**
 * Reads the UnitPrice from a quote.
 *
 * @param {object} quote - Quote DTO.
 * @returns {number|null} UnitPrice or null.
 */
const getQuoteUnitPrice = (quote) => {
  if (!quote) return null;
  return toFiniteNumber(quote.UnitPrice ?? quote.unitPrice);
};

/**
 * Returns the normalized lane id from a lane DTO.
 *
 * @param {object} lane - Lane DTO.
 * @returns {number|null} Lane id.
 */
const getLaneId = (lane) => {
  if (!lane) return null;

  return (
    lane.LaneId ??
    lane.laneId ??
    lane.Id ??
    (lane.lane && (lane.lane.id ?? lane.laneId)) ??
    null
  );
};

/**
 * Returns the normalized source name from a lane DTO.
 *
 * @param {object} lane - Lane DTO.
 * @returns {string} Source label.
 */
const getLaneSource = (lane) => {
  if (!lane) return "";

  return (
    lane.LaneSource ??
    lane.laneSource ??
    lane.sourceLocation ??
    lane.SourceLocation ??
    lane.Origin ??
    lane.origin ??
    (lane.lane && (lane.lane.sourceLocation ?? lane.lane.LaneSource)) ??
    ""
  );
};

/**
 * Returns the normalized destination name from a lane DTO.
 *
 * @param {object} lane - Lane DTO.
 * @returns {string} Destination label.
 */
const getLaneDestination = (lane) => {
  if (!lane) return "";

  return (
    lane.LaneDestination ??
    lane.laneDestination ??
    lane.destinationLocation ??
    lane.DestinationLocation ??
    lane.Destination ??
    lane.destination ??
    (lane.lane &&
      (lane.lane.destinationLocation ?? lane.lane.LaneDestination)) ??
    ""
  );
};

/**
 * Returns true when a lane is considered active.
 *
 * @param {object} lane - Lane DTO.
 * @returns {boolean} True when active.
 */
const isLaneActive = (lane) => {
  if (!lane) return false;

  if (lane.IsActive !== undefined) return !!lane.IsActive;
  if (lane.isActive !== undefined) return !!lane.isActive;

  const relInactive = lane.Inactive ?? lane.inactive;
  const nestedInactive =
    lane.lane && (lane.lane.Inactive ?? lane.lane.inactive);

  if (relInactive !== undefined) return !relInactive;
  if (nestedInactive !== undefined) return !nestedInactive;

  return true;
};

/**
 * Reads the current acceptance state from a quote object.
 *
 * @param {object} quote - Quote DTO.
 * @returns {boolean} Accepted flag.
 */
const getQuoteAccepted = (quote) =>
  quote?.IsAccepted ?? quote?.isAccepted ?? false;

/**
 * Reads the QuoteId or Id from a quote object.
 *
 * @param {object} quote - Quote DTO.
 * @returns {number|null} Quote identifier.
 */
const getQuoteId = (quote) =>
  quote?.QuoteId ?? quote?.quoteId ?? quote?.Id ?? quote?.id ?? null;

/**
 * Reads the LaneId from a quote object regardless of payload casing.
 *
 * This is used to build lane-scoped lookup maps and avoid scanning all quotes
 * for every rendered row.
 *
 * @param {object} quote - Quote DTO.
 * @returns {number|string|null} Lane identifier.
 */
const getQuoteLaneId = (quote) =>
  quote?.LaneId ?? quote?.laneId ?? null;

/**
 * Reads the CarrierId from a quote object regardless of payload casing.
 *
 * This keeps selected-carrier lookups valid even when a carrier was invited but
 * did not submit any quote for the selected target.
 *
 * @param {object} quote - Quote DTO.
 * @returns {number|string|null} Carrier identifier.
 */
const getQuoteCarrierId = (quote) =>
  quote?.CarrierId ?? quote?.carrierId ?? null;

/**
 * Reads the TenderQuoteId from a selected solution award row.
 *
 * @param {object} award - Suggested award DTO.
 * @returns {number|null} Quote identifier.
 */
const getAwardQuoteId = (award) => {
  if (!award) return null;

  return (
    award.TenderQuoteId ??
    award.tenderQuoteId ??
    award.QuoteId ??
    award.quoteId ??
    award.Id ??
    award.id ??
    null
  );
};

/**
 * Reads the Tender currency value regardless of casing/shape.
 *
 * Important:
 * - in TenderPhase mode, the currency still belongs to the parent Tender;
 * - this component already receives tenderInfo from the parent page.
 *
 * Supported shapes:
 * - tenderInfo.Currency = "$"
 * - tenderInfo.currency = "EUR"
 * - tenderInfo.CurrencyCode = "USD"
 * - tenderInfo.Currency = { Symbol, Code, Name }
 *
 * @param {object} tenderInfo - Tender DTO.
 * @returns {string} Printable currency suffix.
 */
const getTenderCurrencyLabel = (tenderInfo) => {
  const rawCurrency =
    tenderInfo?.Currency ??
    tenderInfo?.currency ??
    tenderInfo?.CurrencySymbol ??
    tenderInfo?.currencySymbol ??
    tenderInfo?.CurrencyCode ??
    tenderInfo?.currencyCode ??
    tenderInfo?.CurrencyName ??
    tenderInfo?.currencyName ??
    "";

  if (rawCurrency && typeof rawCurrency === "object") {
    return String(
      rawCurrency.Symbol ??
      rawCurrency.symbol ??
      rawCurrency.Code ??
      rawCurrency.code ??
      rawCurrency.Name ??
      rawCurrency.name ??
      ""
    ).trim();
  }

  return String(rawCurrency ?? "").trim();
};

/**
 * Checks whether a metric is monetary.
 *
 * @param {"unit"|"bid"|"loads"|"etd"} metric - Current metric.
 * @returns {boolean} True when currency must be shown.
 */
const isMoneyMetric = (metric) => metric === "unit" || metric === "bid";

/**
 * Formats a numeric value with the current locale.
 *
 * @param {number} value - Numeric value.
 * @param {string|undefined} locale - UI locale.
 * @returns {string} Formatted number.
 */
const formatNumber = (value, locale) =>
  new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value));

//endregion

/**
 * CarrierResponseDetailTable
 *
 * This component renders a flattened, lane-based table with response details
 * for the currently selected carrier.
 */
export default function CarrierResponseDetailTable({
  tenderInfo = {},
  selectedCarrier = null,
  selectedPhaseId = null,
  selectedSolutionId = null,
  selectedSolution = null,
  selectedSolutionAwards = [],
  selectedSolutionSuggestedQuoteIds = [],
  quotes = [],
  lanes = [],
  isSpotTender = false,
  onRefresh = null,
  bestMetric = "unit",
  laneBestMap = new Map(),
  acceptBusyIds = new Set(),
  onSetQuoteAccepted = null,
  showAcceptedColumn = false,
  showSuggestedColumn = false,
  analysisSolutionReady = false,
}) {
  const { t, i18n } = useTranslation();
  const ctx = useContext(LoadingContext);

  const toastRef = useRef(ctx?.toastMessages);

  useEffect(() => {
    toastRef.current = ctx?.toastMessages;
  }, [ctx?.toastMessages]);

  //region Currency formatting

  /**
   * Currency inherited from the Tender parent.
   *
   * Important:
   * - in TenderPhase context, this must still be the parent Tender currency.
   */
  const currencyLabel = useMemo(
    () => getTenderCurrencyLabel(tenderInfo),
    [tenderInfo]
  );

  const currentLocale = i18n?.resolvedLanguage || i18n?.language || undefined;

  /**
   * Formats a normal non-currency value.
   *
   * @param {number|null} value - Numeric value.
   * @returns {string} Display text.
   */
  const formatPlainValue = useCallback(
    (value) => {
      if (value == null) return "-";

      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) return "-";

      return formatNumber(numericValue, currentLocale);
    },
    [currentLocale]
  );

  /**
   * Formats a currency value.
   *
   * Example:
   * - value: 200
   * - currencyLabel: "$"
   * - result: "200 $"
   *
   * @param {number|null} value - Numeric value.
   * @returns {string} Display text.
   */
  const formatCurrencyValue = useCallback(
    (value) => {
      if (value == null) return "-";

      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) return "-";

      const formatted = formatNumber(numericValue, currentLocale);

      return currencyLabel ? `${formatted} ${currencyLabel}` : formatted;
    },
    [currencyLabel, currentLocale]
  );

  /**
   * Formats a value according to the current metric semantic.
   *
   * @param {number|null} value - Numeric value.
   * @param {"unit"|"bid"|"loads"|"etd"} metric - Metric type.
   * @returns {string} Display text.
   */
  const formatMetricValue = useCallback(
    (value, metric) => {
      if (isMoneyMetric(metric)) {
        return formatCurrencyValue(value);
      }

      return formatPlainValue(value);
    },
    [formatCurrencyValue, formatPlainValue]
  );

  //endregion

  //region Local UI state

  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(10);

  const [anchorEls, setAnchorEls] = useState(null);
  const [anchorV, setAnchorV] = useState(null);
  const [anchorAF, setAnchorAF] = useState(null);

  const [metaPair, setMetaPair] = useState([]);
  const [views, setViews] = useState(new Map([["Default", ""]]));
  // eslint-disable-next-line no-unused-vars
  const [selectedView, setSelectedView] = useState("Default");

  //endregion

  //region Derived rendering flags

  const shouldRenderSuggestedColumn = useMemo(
    () => Boolean(showSuggestedColumn) && Boolean(analysisSolutionReady),
    [showSuggestedColumn, analysisSolutionReady]
  );

  const shouldRenderAcceptedColumn = useMemo(
    () => Boolean(showAcceptedColumn),
    [showAcceptedColumn]
  );

  //endregion

  //region Metadata for advanced filters

  const metaType = useMemo(
    () => [
      {
        metaName: "destination",
        metaDisplay: "Destination",
        metaType: "string",
        metaIsList: false,
      },
      {
        metaName: "origin",
        metaDisplay: "Origin",
        metaType: "string",
        metaIsList: false,
      },
    ],
    []
  );

  //endregion

  //region Filter handlers

  const addMetaPair = (filter) => {
    setMetaPair((prev) => {
      const exists = prev.find((item) => item.name === filter.name);

      return exists
        ? prev.map((item) => (item.name === filter.name ? filter : item))
        : [...prev, filter];
    });
  };

  const deleteFilter = (filterName) => {
    setMetaPair((prev) => prev.filter((item) => item.name !== filterName));
  };

  const deleteAllFilters = () => {
    setMetaPair([]);
  };

  const submitFilters = () => {
    setFirst(0);
  };

  //endregion

  //region Data normalization

  const activeLanes = useMemo(
    () => (lanes || []).filter((lane) => isLaneActive(lane)),
    [lanes]
  );

  const filteredQuotes = useMemo(() => {
    const activeQuotes = (quotes || []).filter((quote) => {
      const inactiveFlag = quote.Inactive ?? quote.inactive;
      return inactiveFlag === undefined || inactiveFlag === false;
    });

    if (isSpotTender) return activeQuotes;

    return activeQuotes.filter((quote) => {
      const phaseId = quote.TenderPhaseId ?? quote.tenderPhaseId;

      if (selectedPhaseId == null) return true;

      return Number(phaseId) === Number(selectedPhaseId);
    });
  }, [quotes, isSpotTender, selectedPhaseId]);

  /**
   * Normalized selected carrier id used by lane-level quote lookup maps.
   */
  const normalizedSelectedCarrierId = useMemo(() => {
    if (selectedCarrier == null) return null;

    const value = Number(selectedCarrier);
    return Number.isFinite(value) ? value : null;
  }, [selectedCarrier]);

  /**
   * Groups active, target-scoped quotes by lane id.
   *
   * This keeps row rendering lightweight because each lane reads its local quote
   * list from a map instead of filtering the complete quote array per row.
   */
  const quotesByLaneId = useMemo(() => {
    const map = new Map();

    for (const quote of filteredQuotes) {
      const rawLaneId = getQuoteLaneId(quote);
      const laneId = rawLaneId != null ? Number(rawLaneId) : null;

      if (!Number.isFinite(laneId)) continue;

      const current = map.get(laneId) || [];
      current.push(quote);
      map.set(laneId, current);
    }

    return map;
  }, [filteredQuotes]);

  /**
   * Resolves the selected carrier quote for each lane.
   *
   * Missing entries represent invited carriers without a submitted quote, so
   * the table can render the lane context with empty commercial values.
   */
  const selectedCarrierQuoteByLaneId = useMemo(() => {
    const map = new Map();

    if (normalizedSelectedCarrierId == null) return map;

    for (const quote of filteredQuotes) {
      const rawLaneId = getQuoteLaneId(quote);
      const rawCarrierId = getQuoteCarrierId(quote);
      const laneId = rawLaneId != null ? Number(rawLaneId) : null;
      const carrierId = rawCarrierId != null ? Number(rawCarrierId) : null;

      if (!Number.isFinite(laneId) || !Number.isFinite(carrierId)) continue;
      if (carrierId !== normalizedSelectedCarrierId) continue;

      if (!map.has(laneId)) {
        map.set(laneId, quote);
      }
    }

    return map;
  }, [filteredQuotes, normalizedSelectedCarrierId]);

  const selectedSolutionSuggestedQuoteIdSet = useMemo(() => {
    const explicitIds = Array.isArray(selectedSolutionSuggestedQuoteIds)
      ? selectedSolutionSuggestedQuoteIds
      : [];

    if (explicitIds.length > 0) {
      return new Set(
        explicitIds
          .map((quoteId) => (quoteId != null ? Number(quoteId) : null))
          .filter((quoteId) => Number.isFinite(quoteId))
      );
    }

    const awardsFromProps = Array.isArray(selectedSolutionAwards)
      ? selectedSolutionAwards
      : [];

    const awardsFromSolution = Array.isArray(selectedSolution?.SuggestedAwards)
      ? selectedSolution.SuggestedAwards
      : Array.isArray(selectedSolution?.suggestedAwards)
        ? selectedSolution.suggestedAwards
        : [];

    const sourceAwards =
      awardsFromProps.length > 0 ? awardsFromProps : awardsFromSolution;

    return new Set(
      sourceAwards
        .map((award) => getAwardQuoteId(award))
        .map((quoteId) => (quoteId != null ? Number(quoteId) : null))
        .filter((quoteId) => Number.isFinite(quoteId))
    );
  }, [
    selectedSolutionSuggestedQuoteIds,
    selectedSolutionAwards,
    selectedSolution,
  ]);

  //endregion

  //region Acceptance rules

  const isLastPhaseSelected = useMemo(() => {
    if (isSpotTender) return true;

    const phases =
      tenderInfo?.Phases ??
      tenderInfo?.phases ??
      tenderInfo?.TenderPhases ??
      tenderInfo?.tenderPhases ??
      [];

    if (Array.isArray(phases) && selectedPhaseId != null) {
      const phase = phases.find((item) => {
        const id = item?.Id ?? item?.id;
        return Number(id) === Number(selectedPhaseId);
      });

      const flag = phase?.IsLastPhase ?? phase?.isLastPhase;
      if (flag !== undefined && flag !== null) return flag === true;
    }

    const match = (quotes || []).find((quote) => {
      const phaseId = quote.TenderPhaseId ?? quote.tenderPhaseId;
      return selectedPhaseId != null
        ? Number(phaseId) === Number(selectedPhaseId)
        : false;
    });

    const phaseObj = match?.TenderPhase ?? match?.tenderPhase ?? null;
    const flag = phaseObj?.IsLastPhase ?? phaseObj?.isLastPhase ?? null;

    return flag === true;
  }, [isSpotTender, tenderInfo, quotes, selectedPhaseId]);

  const quoteSelectionEnabled = isSpotTender || isLastPhaseSelected;

  const quoteSelectionDisabledReason = useMemo(() => {
    if (quoteSelectionEnabled) return "";

    return (
      t(
        "Quote selection is only allowed in the last phase for Non-Spot tenders."
      ) ||
      "Quote selection is only allowed in the last phase for Non-Spot tenders."
    );
  }, [quoteSelectionEnabled, t]);

  //endregion

  //region Lane filtering and paging

  const filteredLanes = useMemo(() => {
    let data = [...activeLanes];

    metaPair.forEach((filter) => {
      if (!filter.value) return;

      const value = String(filter.value).toLowerCase();
      let fieldName = null;

      if (filter.name === "destination") fieldName = "destination";
      if (filter.name === "origin") fieldName = "origin";

      if (!fieldName) return;

      data = data.filter((lane) => {
        const fieldVal =
          fieldName === "origin"
            ? String(getLaneSource(lane)).toLowerCase()
            : String(getLaneDestination(lane)).toLowerCase();

        if (filter.operation === "Contains") {
          return fieldVal.includes(value);
        }

        if (filter.operation === "Equals") {
          return fieldVal === value;
        }

        return true;
      });
    });

    return data;
  }, [activeLanes, metaPair]);

  const pagedLanes = useMemo(() => {
    return filteredLanes.slice(first, first + rows);
  }, [filteredLanes, first, rows]);

  //endregion

  //region Solution-driven suggestion helpers

  const getQuoteSuggestedFromSolution = useCallback(
    (quote) => {
      if (selectedSolutionId == null && !selectedSolution) return false;

      const quoteId = getQuoteId(quote);
      if (quoteId == null) return false;

      return selectedSolutionSuggestedQuoteIdSet.has(Number(quoteId));
    },
    [selectedSolutionId, selectedSolution, selectedSolutionSuggestedQuoteIdSet]
  );

  //endregion

  //region Row striping and cell background helpers

  const getRowBaseBackground = useCallback((rowIndex) => {
    return rowIndex % 2 === 0
      ? "var(--color-white)"
      : "var(--color-grey-light)";
  }, []);

  const resolveMetricBackground = useCallback(
    (background, rowBaseBackground) => {
      if (!background) return rowBaseBackground;

      const normalized = String(background).replace(/\s+/g, "").toLowerCase();

      if (
        normalized === "var(--color-white)" ||
        normalized === "var(--color-grey-light)" ||
        normalized === "white" ||
        normalized === "#fff" ||
        normalized === "#ffffff" ||
        normalized === "transparent" ||
        normalized === "var(--color-grey-white)"
      ) {
        return rowBaseBackground;
      }

      return background;
    },
    []
  );

  //endregion

  //region Lane metrics

  const getLaneMetrics = useCallback(
    (laneId) => {
      const normalizedLaneId = laneId != null ? Number(laneId) : null;
      const laneQuotes = Number.isFinite(normalizedLaneId)
        ? quotesByLaneId.get(normalizedLaneId) || []
        : [];

      let localBestUnitPrice = null;
      let localBestBidValue = null;
      let localBestLoadsValue = null;
      let localBestEtdValue = null;

      for (const quote of laneQuotes) {
        const unitPrice = getQuoteMetricValue(quote, "unit");
        const bidValue = getQuoteMetricValue(quote, "bid");
        const loads = getQuoteMetricValue(quote, "loads");
        const etd = getQuoteMetricValue(quote, "etd");

        if (unitPrice != null) {
          localBestUnitPrice =
            localBestUnitPrice == null
              ? unitPrice
              : Math.min(localBestUnitPrice, unitPrice);
        }

        if (bidValue != null) {
          localBestBidValue =
            localBestBidValue == null
              ? bidValue
              : Math.min(localBestBidValue, bidValue);
        }

        if (loads != null) {
          localBestLoadsValue =
            localBestLoadsValue == null
              ? loads
              : Math.max(localBestLoadsValue, loads);
        }

        if (etd != null) {
          localBestEtdValue =
            localBestEtdValue == null ? etd : Math.min(localBestEtdValue, etd);
        }
      }

      const global =
        laneBestMap && Number.isFinite(normalizedLaneId)
          ? laneBestMap.get(normalizedLaneId)
          : null;

      const bestUnitPrice =
        toFiniteNumber(
          global?.bestUnitValue ??
          global?.bestUnitPrice ??
          global?.minUnitPrice
        ) ??
        localBestUnitPrice ??
        null;

      const bestBidValue =
        toFiniteNumber(global?.bestBidValue ?? global?.minBidValue) ??
        localBestBidValue ??
        null;

      const bestLoadsValue =
        toFiniteNumber(global?.bestLoadsValue ?? global?.maxLoads) ??
        localBestLoadsValue ??
        null;

      const bestEtdValue =
        toFiniteNumber(
          global?.bestEtdValue ?? global?.minEstimatedTransitDays
        ) ??
        localBestEtdValue ??
        null;

      let bestValue = null;

      switch (bestMetric) {
        case "unit":
          bestValue = bestUnitPrice;
          break;

        case "bid":
          bestValue = bestBidValue;
          break;

        case "loads":
          bestValue = bestLoadsValue;
          break;

        case "etd":
          bestValue = bestEtdValue;
          break;

        default:
          bestValue = bestUnitPrice;
          break;
      }

      const carrierQuote = Number.isFinite(normalizedLaneId)
        ? selectedCarrierQuoteByLaneId.get(normalizedLaneId) || null
        : null;

      const carrierUnitPrice = getQuoteUnitPrice(carrierQuote);
      const carrierBidValue = getQuoteBidValue(carrierQuote);
      const carrierLoads = getQuoteMetricValue(carrierQuote, "loads");
      const carrierEtd = getQuoteMetricValue(carrierQuote, "etd");

      return {
        bestValue,
        bestUnitPrice,
        bestBidValue,
        bestLoadsValue,
        bestEtdValue,
        carrierUnitPrice,
        carrierBidValue,
        carrierLoads,
        carrierEtd,
      };
    },
    [quotesByLaneId, selectedCarrierQuoteByLaneId, bestMetric, laneBestMap]
  );

  const getSelectedCarrierQuoteForLane = useCallback(
    (laneId) => {
      const normalizedLaneId = laneId != null ? Number(laneId) : null;

      if (!Number.isFinite(normalizedLaneId)) return null;

      return selectedCarrierQuoteByLaneId.get(normalizedLaneId) || null;
    },
    [selectedCarrierQuoteByLaneId]
  );

  //endregion

  //region Accept / unaccept action

  const setQuoteAccepted = useCallback(
    async ({ quoteId, nextAccepted }) => {
      if (!quoteId) return;

      if (typeof onSetQuoteAccepted === "function") {
        onSetQuoteAccepted({ quoteId, nextAccepted });
        return;
      }

      toastRef.current?.(
        "error",
        "",
        t("This action is not available in the current context.") ||
        "This action is not available in the current context."
      );
    },
    [onSetQuoteAccepted, t]
  );

  //endregion

  //region Dynamic columns

  const allColumnsList = useMemo(() => {
    const columns = [
      "Origin",
      "Destination",
      "Best",
      "UnitPrice",
      "BidValue",
      "Loads",
      "EstimatedTransitDays",
    ];

    if (shouldRenderSuggestedColumn) {
      columns.push("IsSuggested");
    }

    if (shouldRenderAcceptedColumn) {
      columns.push("Accept");
    }

    return columns;
  }, [shouldRenderSuggestedColumn, shouldRenderAcceptedColumn]);

  const [selectedColumns, setSelectedColumns] = useState(allColumnsList);

  useEffect(() => {
    setSelectedColumns((prev) => {
      const kept = prev.filter((column) => allColumnsList.includes(column));
      const missing = allColumnsList.filter(
        (column) => !kept.includes(column)
      );
      return [...kept, ...missing];
    });
  }, [allColumnsList]);

  const bestColumnHeader = useMemo(() => {
    switch (bestMetric) {
      case "unit":
        return t("BestUnitValue") || "Best Unit Value";

      case "bid":
        return t("BestBidValue") || "Best Bid Value";

      case "loads":
        return t("MoreLoads") || "More Loads";

      case "etd":
        return t("BestEstimatedTransitDays") || "Best Estimated Transit Days";

      default:
        return t("BestMetric") || "Best Metric";
    }
  }, [bestMetric, t]);

  //endregion

  //region Export

  const exportExcel = async () => {
    const singles = [
      { label: "Origin" },
      { label: "Destination" },
      { label: "Best" },
      { label: "Unit Price" },
      { label: "Bid Value" },
      { label: "Loads" },
      { label: "Estimated Transit Days" },
    ];

    if (shouldRenderSuggestedColumn) {
      singles.push({ label: "Is Suggested" });
    }

    if (shouldRenderAcceptedColumn) {
      singles.push({ label: "Accept" });
    }

    await exportGroupedExcel({
      sheetName: "CarrierResponses",
      filename: "CarrierResponses.xlsx",
      singles,
      groups: [],
      data: filteredLanes,
      buildRow: (lane) => {
        const laneId = getLaneId(lane);
        const {
          bestValue,
          carrierUnitPrice,
          carrierBidValue,
          carrierLoads,
          carrierEtd,
        } = getLaneMetrics(laneId);

        const carrierQuote = getSelectedCarrierQuoteForLane(laneId);
        const suggested = getQuoteSuggestedFromSolution(carrierQuote);
        const accepted = getQuoteAccepted(carrierQuote);

        const row = [
          getLaneSource(lane),
          getLaneDestination(lane),
          formatMetricValue(bestValue, bestMetric),
          formatCurrencyValue(carrierUnitPrice),
          formatCurrencyValue(carrierBidValue),
          formatMetricValue(carrierLoads, "loads"),
          formatMetricValue(carrierEtd, "etd"),
        ];

        if (shouldRenderSuggestedColumn) {
          row.push(suggested ? "Yes" : "No");
        }

        if (shouldRenderAcceptedColumn) {
          row.push(accepted ? "Yes" : "No");
        }

        return row;
      },
    });
  };

  //endregion

  //region Busy state helper

  const isBusy = useCallback(
    (quoteId) => {
      if (!quoteId) return false;
      if (!acceptBusyIds) return false;
      if (acceptBusyIds instanceof Set) return acceptBusyIds.has(quoteId);
      if (Array.isArray(acceptBusyIds)) return acceptBusyIds.includes(quoteId);
      return false;
    },
    [acceptBusyIds]
  );

  //endregion

  //region Render

  return (
    <div className="carrier-response-detail-root">
      <div className="carrier-response-detail-header">
        <TableHeader
          filterCount={metaPair.length}
          onRefresh={onRefresh}
          onExport={exportExcel}
          onOpenViews={(e) => setAnchorV(e.currentTarget)}
          onOpenFilters={(e) => setAnchorAF(e.currentTarget)}
          onOpenColumns={(e) => setAnchorEls(e.currentTarget)}
        />
      </div>

      <ColumnMenu
        anchorEl={anchorEls}
        open={Boolean(anchorEls)}
        onClose={() => setAnchorEls(null)}
        singleColumns={allColumnsList}
        columnGroups={[]}
        selectedColumns={selectedColumns}
        onChangeSelected={setSelectedColumns}
      />

      <ViewsMenu
        anchorEl={anchorV}
        open={Boolean(anchorV)}
        onClose={() => setAnchorV(null)}
        viewsMap={views}
        onSelectDefault={() => {
          setSelectedView("Default");
          setMetaPair([]);
          setAnchorV(null);
        }}
        onSelectView={(view) => {
          const str = views.get(view);

          if (str) {
            try {
              setMetaPair(JSON.parse(str));
              setSelectedView(view);
            } catch {
              // Ignore invalid saved view payloads.
            }
          }

          setAnchorV(null);
        }}
        onCreateNewViewClick={() => {
          setAnchorV(null);
          setAnchorAF(document.getElementById("advFilter"));
        }}
      />

      <AdvanceFilter
        metaPair={metaPair}
        metaType={metaType}
        onAddMetaPair={addMetaPair}
        onSubmitFilters={submitFilters}
        onDeleteFilter={deleteFilter}
        onDeleteAllFilters={deleteAllFilters}
        anchorAF={anchorAF}
        setAnchorAF={setAnchorAF}
        onSaveFilter={(viewName) => {
          const filterUrl = JSON.stringify(metaPair);

          setViews((prev) => {
            const next = new Map(prev);
            next.set(viewName, filterUrl);
            return next;
          });

          setSelectedView(viewName);
        }}
        views={views}
        onToasts={() => { }}
        color="var(--color-red)"
        removeFilters={["Add"]}
      />

      <div className="carrier-response-detail-table-wrapper">
        <table className="carrier-response-detail-table">
          <thead>
            <tr>
              {selectedColumns.includes("Origin") && (
                <th
                  className="carrier-response-detail-col-origin"
                  style={headerCellStyle()}
                >
                  {t("Origin")}
                </th>
              )}

              {selectedColumns.includes("Destination") && (
                <th
                  className="carrier-response-detail-col-destination"
                  style={headerCellStyle()}
                >
                  {t("Destination")}
                </th>
              )}

              {selectedColumns.includes("Best") && (
                <th
                  className="carrier-response-detail-col-metric"
                  style={headerCellStyle()}
                >
                  {bestColumnHeader}
                </th>
              )}

              {selectedColumns.includes("UnitPrice") && (
                <th
                  className="carrier-response-detail-col-metric"
                  style={headerCellStyle()}
                >
                  {t("UnitPrice") || "Unit Price"}
                </th>
              )}

              {selectedColumns.includes("BidValue") && (
                <th
                  className="carrier-response-detail-col-metric"
                  style={headerCellStyle()}
                >
                  {t("BidValue") || "Bid Value"}
                </th>
              )}

              {selectedColumns.includes("Loads") && (
                <th
                  className="carrier-response-detail-col-metric"
                  style={headerCellStyle()}
                >
                  {t("Loads")}
                </th>
              )}

              {selectedColumns.includes("EstimatedTransitDays") && (
                <th
                  className="carrier-response-detail-col-metric"
                  style={headerCellStyle()}
                >
                  {t("EstimatedTransitDays") || "Estimated Transit Days"}
                </th>
              )}

              {selectedColumns.includes("IsSuggested") && (
                <th
                  className="carrier-response-detail-col-flag"
                  style={headerCellStyle()}
                >
                  {t("IsSuggested") || "Is Suggested"}
                </th>
              )}

              {selectedColumns.includes("Accept") && (
                <th
                  className="carrier-response-detail-col-flag"
                  style={headerCellStyle()}
                >
                  {t("Accept") || "Accept"}
                </th>
              )}
            </tr>
          </thead>

          <tbody>
            {pagedLanes.map((lane, rowIndex) => {
              const laneId = getLaneId(lane);
              const rowBaseBackground = getRowBaseBackground(rowIndex);

              const {
                bestValue,
                carrierUnitPrice,
                carrierBidValue,
                carrierLoads,
                carrierEtd,
              } = getLaneMetrics(laneId);

              const rawBestBg =
                bestValue != null
                  ? getCellColor(bestValue, bestValue)
                  : rowBaseBackground;

              const bestBg = resolveMetricBackground(
                rawBestBg,
                rowBaseBackground
              );

              let rawComparisonBg = rowBaseBackground;

              if (bestValue != null) {
                if (bestMetric === "unit" && carrierUnitPrice != null) {
                  rawComparisonBg = getCellColor(carrierUnitPrice, bestValue);
                } else if (bestMetric === "bid" && carrierBidValue != null) {
                  rawComparisonBg = getCellColor(carrierBidValue, bestValue);
                } else if (bestMetric === "etd" && carrierEtd != null) {
                  rawComparisonBg = getCellColor(carrierEtd, bestValue);
                } else if (bestMetric === "loads" && carrierLoads != null) {
                  rawComparisonBg = getCellColor(bestValue, carrierLoads);
                }
              }

              const comparisonBg = resolveMetricBackground(
                rawComparisonBg,
                rowBaseBackground
              );

              const carrierQuote = getSelectedCarrierQuoteForLane(laneId);
              const quoteId = getQuoteId(carrierQuote);
              const suggested = getQuoteSuggestedFromSolution(carrierQuote);
              const accepted = getQuoteAccepted(carrierQuote);

              const disabled =
                !quoteSelectionEnabled || !quoteId || isBusy(quoteId);

              const tooltip = !quoteSelectionEnabled
                ? quoteSelectionDisabledReason
                : !quoteId
                  ? t("No quote found for this lane.") ||
                  "No quote found for this lane."
                  : "";

              return (
                <tr
                  key={
                    laneId ??
                    `${getLaneSource(lane)}-${getLaneDestination(lane)}`
                  }
                >
                  {selectedColumns.includes("Origin") && (
                    <td
                      className="carrier-response-detail-col-origin"
                      style={cellStyle({
                        background: rowBaseBackground,
                        fontWeight: 700,
                      })}
                    >
                      {getLaneSource(lane)}
                    </td>
                  )}

                  {selectedColumns.includes("Destination") && (
                    <td
                      className="carrier-response-detail-col-destination"
                      style={cellStyle({
                        background: rowBaseBackground,
                        fontWeight: 700,
                      })}
                    >
                      {getLaneDestination(lane)}
                    </td>
                  )}

                  {selectedColumns.includes("Best") && (
                    <td
                      className="carrier-response-detail-col-metric"
                      style={cellStyle({ background: bestBg })}
                    >
                      {formatMetricValue(bestValue, bestMetric)}
                    </td>
                  )}

                  {selectedColumns.includes("UnitPrice") && (
                    <td
                      className="carrier-response-detail-col-metric"
                      style={cellStyle({
                        background:
                          bestMetric === "unit"
                            ? comparisonBg
                            : rowBaseBackground,
                      })}
                    >
                      {formatCurrencyValue(carrierUnitPrice)}
                    </td>
                  )}

                  {selectedColumns.includes("BidValue") && (
                    <td
                      className="carrier-response-detail-col-metric"
                      style={cellStyle({
                        background:
                          bestMetric === "bid"
                            ? comparisonBg
                            : rowBaseBackground,
                      })}
                    >
                      {formatCurrencyValue(carrierBidValue)}
                    </td>
                  )}

                  {selectedColumns.includes("Loads") && (
                    <td
                      className="carrier-response-detail-col-metric"
                      style={cellStyle({
                        background:
                          bestMetric === "loads"
                            ? comparisonBg
                            : rowBaseBackground,
                      })}
                    >
                      {formatMetricValue(carrierLoads, "loads")}
                    </td>
                  )}

                  {selectedColumns.includes("EstimatedTransitDays") && (
                    <td
                      className="carrier-response-detail-col-metric"
                      style={cellStyle({
                        background:
                          bestMetric === "etd"
                            ? comparisonBg
                            : rowBaseBackground,
                      })}
                    >
                      {formatMetricValue(carrierEtd, "etd")}
                    </td>
                  )}

                  {selectedColumns.includes("IsSuggested") && (
                    <td
                      className="carrier-response-detail-col-flag"
                      style={cellStyle({ background: rowBaseBackground })}
                    >
                      <MCheckBox
                        label={null}
                        checked={!!suggested}
                        disabled={true}
                        noPadding={true}
                        fontSize={"22px"}
                        onChange={() => { }}
                      />
                    </td>
                  )}

                  {selectedColumns.includes("Accept") && (
                    <td
                      className="carrier-response-detail-col-flag"
                      style={cellStyle({ background: rowBaseBackground })}
                    >
                      <MCheckBox
                        label={null}
                        checked={!!accepted}
                        disabled={disabled}
                        tooltipName={tooltip}
                        noPadding={true}
                        fontSize={"22px"}
                        onChange={(e) => {
                          const nextAccepted = !!e?.target?.checked;

                          if (!quoteSelectionEnabled) return;
                          if (!quoteId) return;

                          setQuoteAccepted({ quoteId, nextAccepted });
                        }}
                      />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="carrier-response-detail-paginator">
        <Paginator
          first={first}
          rows={rows}
          totalRecords={filteredLanes.length}
          rowsPerPageOptions={[10, 20, 50]}
          onPageChange={(e) => {
            setFirst(e.first);
            setRows(e.rows);
          }}
          template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        />
      </div>
    </div>
  );

  //endregion
}

CarrierResponseDetailTable.propTypes = {
  tenderInfo: PropTypes.object,
  selectedCarrier: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  selectedPhaseId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

  selectedSolutionId: PropTypes.number,
  selectedSolution: PropTypes.object,
  selectedSolutionAwards: PropTypes.array,
  selectedSolutionSuggestedQuoteIds: PropTypes.arrayOf(PropTypes.number),

  quotes: PropTypes.array,
  lanes: PropTypes.array,
  isSpotTender: PropTypes.bool,
  onRefresh: PropTypes.func,

  bestMetric: PropTypes.oneOf(["unit", "bid", "loads", "etd"]),
  laneBestMap: PropTypes.instanceOf(Map),

  acceptBusyIds: PropTypes.oneOfType([
    PropTypes.instanceOf(Set),
    PropTypes.arrayOf(PropTypes.number),
  ]),

  onSetQuoteAccepted: PropTypes.func,
  showAcceptedColumn: PropTypes.bool,
  showSuggestedColumn: PropTypes.bool,
  analysisSolutionReady: PropTypes.bool,
};